import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Leaf, LogOut, Loader2, Phone, Mail, Calendar, Clock, Video, MapPin, Check, X as XIcon, RotateCcw, StickyNote, CalendarClock, BellRing, BellOff, MessageCircle, Sparkles } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AvailabilityManager } from "../components/admin/AvailabilityManager";
import { ProgramsManager } from "../components/admin/ProgramsManager";

const STATUS_STYLE = {
  pending: "bg-[#F7EBC6] text-[#7a5f14] border-[#EADB9B]",
  confirmed: "bg-[#D8E5ED] text-[#245a78] border-[#B8D1E0]",
  completed: "bg-[#DFF0C4] text-[#3A7D32] border-[#C9DFAC]",
  cancelled: "bg-[#FFE3C8] text-[#B0500F] border-[#FFC9A0]",
};

const waReminderLink = (a) => {
  const digits = a.phone.replace(/\D/g, "");
  const to = digits.length === 10 ? `91${digits}` : digits;
  const msg = `Hi ${a.name.split(" ")[0]}, a gentle reminder from Serene Mind Place — your ${a.consultation_type} session with Dr. Pratyusha is on ${a.date} at ${a.time_slot}. See you then!`;
  return `https://wa.me/${to}?text=${encodeURIComponent(msg)}`;
};

function NotesEditor({ appt }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(appt.notes || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.patch(`/appointments/${appt.id}/notes`, { notes }); toast.success("Private note saved"); }
    catch { toast.error("Failed to save note"); }
    finally { setSaving(false); }
  };
  return (
    <div className="mt-4 pt-3 border-t border-[#DFF0C4]">
      <button data-testid={`notes-toggle-${appt.id}`} onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1.5 text-xs text-[#55703F] hover:text-[#3A7D32]">
        <StickyNote size={13} /> Private intake notes{appt.notes && !open ? " · saved" : ""}
      </button>
      {open && (
        <div className="mt-2">
          <textarea data-testid={`notes-input-${appt.id}`} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
            placeholder="Confidential notes for this session — visible only to you"
            className="w-full rounded-xl border border-[#C9DFAC] bg-[#F2F8E8] px-3 py-2 text-sm outline-none focus:border-[#3A7D32] resize-none" />
          <button data-testid={`notes-save-${appt.id}`} onClick={save} disabled={saving}
            className="mt-1 rounded-full bg-[#3A7D32] text-[#F2F8E8] px-4 py-1.5 text-xs hover:bg-[#20301A] disabled:opacity-60">
            {saving ? "Saving…" : "Save note"}
          </button>
        </div>
      )}
    </div>
  );
}

function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try { await login(email, password); toast.success("Welcome back"); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center px-4 bg-[#F2F8E8]">
      <div className="grain" />
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md rounded-[28px] bg-white soft-shadow border border-[#C9DFAC] p-8">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-10 h-10 rounded-full bg-[#3A7D32] text-[#F2F8E8]"><Leaf size={18} /></span>
          <div><div className="font-serif text-xl text-[#20301A]">Serene Mind Place</div><div className="overline text-[0.6rem]">Practitioner Dashboard</div></div>
        </div>
        <h1 className="font-serif text-3xl mt-7 text-[#20301A]">Welcome back</h1>
        <p className="text-sm text-[#55703F] mt-1">Sign in to manage your appointments.</p>
        <form onSubmit={submit} className="mt-6 space-y-3">
          <input data-testid="admin-email-input" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-xl border border-[#C9DFAC] bg-[#F2F8E8] px-4 py-3 outline-none focus:border-[#3A7D32]" />
          <input data-testid="admin-password-input" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-xl border border-[#C9DFAC] bg-[#F2F8E8] px-4 py-3 outline-none focus:border-[#3A7D32]" />
          {error && <p data-testid="admin-login-error" className="text-sm text-[#B0500F]">{error}</p>}
          <button data-testid="admin-login-submit" disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#3A7D32] text-[#F2F8E8] px-6 py-3 text-sm hover:bg-[#20301A] transition-colors disabled:opacity-60">
            {loading ? <Loader2 size={16} className="animate-spin" /> : "Sign In"}
          </button>
        </form>
        <Link to="/" className="mt-5 inline-block text-sm text-[#55703F] hover:text-[#3A7D32]">← Back to website</Link>
      </motion.div>
    </div>
  );
}

function Dashboard() {
  const { user, logout } = useAuth();
  const [appts, setAppts] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("appointments");

  const load = async () => {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([
        api.get(`/appointments${filter === "all" ? "" : `?status=${filter}`}`),
        api.get("/appointments/stats"),
      ]);
      setAppts(a.data); setStats(s.data);
    } catch (e) { toast.error("Failed to load appointments"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/appointments/${id}/status`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) { toast.error("Update failed"); }
  };

  const STAT_CARDS = stats ? [
    { label: "Total Requests", value: stats.total, tone: "bg-[#DFF0C4]" },
    { label: "Pending", value: stats.pending, tone: "bg-[#F7EBC6]/70" },
    { label: "Confirmed", value: stats.confirmed, tone: "bg-[#D8E5ED]/70" },
    { label: "Online / In-Person", value: `${stats.online} / ${stats.in_person}`, tone: "bg-[#FFEFDF]" },
  ] : [];

  return (
    <div className="min-h-screen bg-[#F2F8E8]">
      <div className="grain" />
      <header className="glass sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="grid place-items-center w-9 h-9 rounded-full bg-[#3A7D32] text-[#F2F8E8]"><Leaf size={16} /></span>
            <div><div className="font-serif text-lg text-[#20301A] leading-tight">Dashboard</div><div className="text-[0.7rem] text-[#55703F]">{user?.email}</div></div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="admin-refresh" className="grid place-items-center w-9 h-9 rounded-full border border-[#C9DFAC] text-[#3A7D32] hover:bg-[#DFF0C4]"><RotateCcw size={15} /></button>
            <Link to="/" className="text-sm text-[#55703F] hover:text-[#3A7D32] px-2">View site</Link>
            <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-1.5 rounded-full bg-[#3A7D32] text-[#F2F8E8] px-4 py-2 text-sm hover:bg-[#20301A]"><LogOut size={14} /> Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="font-serif text-3xl sm:text-4xl text-[#20301A]">{tab === "appointments" ? "Appointments" : tab === "availability" ? "Availability" : "Offers & Workshops"}</h1>
          <div className="inline-flex rounded-full border border-[#C9DFAC] bg-white p-1">
            <button data-testid="admin-tab-appointments" onClick={() => setTab("appointments")}
              className={`rounded-full px-4 py-2 text-xs transition-all ${tab === "appointments" ? "bg-[#3A7D32] text-[#F2F8E8]" : "text-[#55703F]"}`}>Appointments</button>
            <button data-testid="admin-tab-availability" onClick={() => setTab("availability")}
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs transition-all ${tab === "availability" ? "bg-[#3A7D32] text-[#F2F8E8]" : "text-[#55703F]"}`}><CalendarClock size={13} /> Availability</button>
            <button data-testid="admin-tab-programmes" onClick={() => setTab("programmes")}
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs transition-all ${tab === "programmes" ? "bg-[#D98BA0] text-white" : "text-[#55703F]"}`}><Sparkles size={13} /> Offers & Workshops</button>
          </div>
        </div>
        {tab === "appointments" ? (<>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          {STAT_CARDS.map((c) => (
            <div key={c.label} data-testid={`stat-${c.label.split(" ")[0].toLowerCase()}`} className={`rounded-2xl border border-[#C9DFAC] p-5 ${c.tone}`}>
              <div className="text-3xl font-serif text-[#20301A]">{c.value}</div>
              <div className="text-xs text-[#55703F] mt-1 font-mono uppercase tracking-widest">{c.label}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mt-8 mb-4">
          {["all", "pending", "confirmed", "completed", "cancelled"].map((f) => (
            <button key={f} data-testid={`admin-filter-${f}`} onClick={() => setFilter(f)}
              className={`rounded-full px-4 py-2 text-xs capitalize border transition-all ${filter === f ? "bg-[#3A7D32] text-[#F2F8E8] border-[#3A7D32]" : "border-[#C9DFAC] text-[#55703F] hover:border-[#3A7D32]"}`}>{f}</button>
          ))}
        </div>

        {loading ? (
          <div className="py-20 grid place-items-center text-[#55703F]"><Loader2 className="animate-spin" /></div>
        ) : appts.length === 0 ? (
          <div className="py-20 text-center text-[#55703F] rounded-2xl border border-dashed border-[#C9DFAC] bg-[#DFF0C4]/40">No appointments in this view yet.</div>
        ) : (
          <div className="grid gap-3" data-testid="admin-appointments-table">
            {appts.map((a) => (
              <motion.div key={a.id} layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                data-testid={`appt-row-${a.id}`}
                className="rounded-2xl bg-white border border-[#C9DFAC] p-5">
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="font-serif text-lg text-[#20301A]">{a.name}</span>
                    <span className={`text-[0.65rem] px-2.5 py-1 rounded-full border font-mono uppercase tracking-wider ${STATUS_STYLE[a.status]}`}>{a.status}</span>
                    {a.payment_status === "paid" && (
                      <span data-testid={`paid-${a.id}`} className="inline-flex items-center gap-1 text-[0.65rem] px-2.5 py-1 rounded-full border border-[#C9DFAC] bg-white text-[#3A7D32] font-mono uppercase tracking-wider">₹ Paid</span>
                    )}
                    {a.reminder_sent && (
                      <span data-testid={`reminder-sent-${a.id}`} className="inline-flex items-center gap-1 text-[0.65rem] px-2.5 py-1 rounded-full border border-[#C9DFAC] bg-[#DFF0C4] text-[#3A7D32] font-mono uppercase tracking-wider"><BellRing size={10} /> Reminded</span>
                    )}
                    {!a.reminder_sent && a.reminder_attempts >= 3 && (
                      <span data-testid={`reminder-failed-${a.id}`} className="inline-flex items-center gap-1 text-[0.65rem] px-2.5 py-1 rounded-full border border-[#FFC9A0] bg-[#FFE3C8] text-[#B0500F] font-mono uppercase tracking-wider"><BellOff size={10} /> Reminder failed</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-xs text-[#55703F]">
                    <span className="inline-flex items-center gap-1"><Mail size={12} /> {a.email}</span>
                    <span className="inline-flex items-center gap-1"><Phone size={12} /> {a.phone}</span>
                    <span className="inline-flex items-center gap-1">{a.consultation_type.includes("Online") ? <Video size={12} /> : <MapPin size={12} />} {a.consultation_type}</span>
                    <span className="inline-flex items-center gap-1"><Calendar size={12} /> {a.date}</span>
                    <span className="inline-flex items-center gap-1"><Clock size={12} /> {a.time_slot}</span>
                  </div>
                  <p className="mt-2 text-sm text-[#3C5A2E]"><span className="text-[#55703F]">Focus:</span> {a.concern}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <a data-testid={`whatsapp-${a.id}`} href={waReminderLink(a)} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 rounded-full border border-[#C9DFAC] bg-[#DFF0C4]/60 text-[#3A7D32] px-3.5 py-2 text-xs hover:bg-[#DFF0C4]"><MessageCircle size={13} /> WhatsApp</a>
                  {a.status !== "confirmed" && <button data-testid={`confirm-${a.id}`} onClick={() => setStatus(a.id, "confirmed")} className="inline-flex items-center gap-1 rounded-full bg-[#3A7D32] text-[#F2F8E8] px-3.5 py-2 text-xs hover:bg-[#20301A]"><Check size={13} /> Confirm</button>}
                  {a.status !== "completed" && <button data-testid={`complete-${a.id}`} onClick={() => setStatus(a.id, "completed")} className="rounded-full border border-[#C9DFAC] text-[#3A7D32] px-3.5 py-2 text-xs hover:bg-[#DFF0C4]">Complete</button>}
                  {a.status !== "cancelled" && <button data-testid={`cancel-${a.id}`} onClick={() => setStatus(a.id, "cancelled")} className="inline-flex items-center gap-1 rounded-full border border-[#FFC9A0] text-[#B0500F] px-3.5 py-2 text-xs hover:bg-[#FFE3C8]"><XIcon size={13} /> Cancel</button>}
                </div>
                </div>
                <NotesEditor appt={a} />
              </motion.div>
            ))}
          </div>
        )}
        </>) : tab === "availability" ? (
          <AvailabilityManager />
        ) : (
          <ProgramsManager />
        )}
      </main>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center bg-[#F2F8E8] text-[#55703F]"><Loader2 className="animate-spin" /></div>;
  return user ? <Dashboard /> : <Login />;
}
